import { Button, Divider, Grid, IconButton, Skeleton, Typography } from '@mui/material';
import { Box, Stack } from '@mui/system';
import { format } from 'date-fns';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router';
import { paths, permissions } from 'src/paths';
import { CloseIcon, PencilIcon } from './EocDetailsDrawer';
import { formatContactNumber } from './patient-info';
import { formatPatientData } from 'src/layouts/components/utils-method';

type Props = {
  handleClosePatientInfoDrawer: any;
  patientId: any;
};

const PatientInfoDrawer = (props: Props) => {
  const { handleClosePatientInfoDrawer, patientId } = props;
  const { UPDATE_PATIENT } = permissions;
  const navigate = useNavigate();
  const { patients, editPatient } = paths;

  const permissionData = useSelector(
    (state: any) => state?.getTherapistProfileReducer?.response?.data?.currentPermissions
  );
  const patientData = useSelector((state: any) => state?.getSinglePatientReducer?.patient);
  const patientDob = patientData?.dateOfBirth && patientData?.dateOfBirth?.split('T')?.at(0);
  const patientDataLoader = useSelector((state: any) => state.getSinglePatientReducer);

  return (
    <Grid
      flexDirection={'column'}
      justifyContent={'space-between'}
      container
      sx={{ height: '100%' }}
    >
      <>
        <Grid xs={12}>
          <Grid
            container
            sx={{ width: '100%' }}
          >
            <Grid
              py={2}
              px={2}
              xs={12}
              sx={{ width: '100%' }}
            >
              {' '}
              <Stack
                direction="row"
                alignItems={'center'}
                justifyContent={'space-between'}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Typography variant="h5">Patient Details </Typography>
                </Box>
                <IconButton onClick={handleClosePatientInfoDrawer}>
                  <CloseIcon />
                </IconButton>
              </Stack>
              <Divider sx={{ mt: 2.5 }} />
              <Stack p={2}>
                <Stack
                  flexDirection="row"
                  justifyContent="space-between"
                  alignItems={'center'}
                >
                  <Typography
                    variant="h6"
                    sx={{ color: '#4338CA' }}
                  >
                    Personal Info
                  </Typography>
                  <Box>
                    <Button
                      variant="text"
                      startIcon={<PencilIcon />}
                      onClick={(event) =>
                        navigate(`${patients}${editPatient}`, {
                          state: {
                            patientId,
                            patientData,
                          },
                        })
                      }
                      color="inherit"
                      disabled={!permissionData?.includes(UPDATE_PATIENT)}
                    >
                      Edit
                    </Button>
                  </Box>
                </Stack>

                <Box>
                  {patientDataLoader?.loading ? (
                    <Box mt={1}>
                      {' '}
                      {['1', '2', '3'].map((sle) => {
                        return (
                          <Box
                            sx={{ width: '100%', display: 'flex', justifyContent: 'space-between' }}
                            key={sle}
                          >
                            <Typography
                              width={'20%'}
                              variant="h1"
                            >
                              <Skeleton animation={false} />
                            </Typography>
                            <Typography
                              width={'75%'}
                              variant="h1"
                            >
                              <Skeleton animation={false} />
                            </Typography>
                          </Box>
                        );
                      })}
                    </Box>
                  ) : (
                    <Box>
                      <Stack>
                        <Typography
                          sx={{ color: '#6C737F' }}
                          variant="overline"
                        >
                          Basic Details
                        </Typography>
                        <Box mt={2}>
                          <Stack
                            flexDirection="row"
                            justifyContent="space-between"
                            py={1}
                          >
                            <Stack>
                              <Typography
                                noWrap
                                variant="subtitle2"
                              >
                                Activation Status
                              </Typography>
                            </Stack>
                            <Stack sx={{ width: '50%' }}>
                              <Typography
                                variant="body2"
                                sx={{ color: '#6C737F' }}
                                noWrap
                              >
                                {patientData?.active ? 'Active' : 'Inactive'}
                              </Typography>
                            </Stack>
                          </Stack>

                          <Stack
                            flexDirection="row"
                            justifyContent="space-between"
                            py={1}
                          >
                            <Stack>
                              <Typography variant="subtitle2">Gender</Typography>
                            </Stack>
                            <Stack sx={{ width: '50%' }}>
                              <Typography
                                color="text.secondary"
                                sx={{
                                  textTransform: 'capitalize',
                                }}
                                variant="body2"
                              >
                                {patientData?.gender ? patientData?.gender?.toLowerCase() : '-'}
                              </Typography>
                            </Stack>
                          </Stack>

                          <Stack
                            flexDirection="row"
                            justifyContent="space-between"
                            py={1}
                          >
                            <Stack>
                              <Typography variant="subtitle2">Date Of Birth</Typography>
                            </Stack>
                            <Stack sx={{ width: '50%' }}>
                              <Typography
                                variant="body2"
                                sx={{ color: '#6C737F' }}
                              >
                                {patientData?.dateOfBirth
                                  ? format(
                                      new Date(
                                        new Date(patientDob).valueOf() +
                                          new Date(patientDob).getTimezoneOffset() * 60 * 1000
                                      ),
                                      'MMM dd, yyyy'
                                    )
                                  : '-'}
                              </Typography>
                            </Stack>
                          </Stack>

                          <Stack
                            flexDirection="row"
                            justifyContent="space-between"
                            py={1}
                          >
                            <Stack>
                              <Typography variant="subtitle2">Phone Number</Typography>
                            </Stack>
                            <Stack sx={{ width: '50%' }}>
                              <Typography
                                variant="body2"
                                sx={{ color: '#6C737F' }}
                              >
                                {patientData?.patientPhoneNo
                                  ? formatContactNumber(patientData?.patientPhoneNo).formattedValue
                                  : '-'}
                              </Typography>
                            </Stack>
                          </Stack>

                          <Stack
                            flexDirection="row"
                            justifyContent="space-between"
                            py={1}
                          >
                            <Stack>
                              <Typography variant="subtitle2">Email</Typography>
                            </Stack>
                            <Stack sx={{ width: '50%' }}>
                              <Typography
                                variant="body2"
                                sx={{ color: '#6C737F' }}
                              >
                                {patientData?.patientEmail ? patientData?.patientEmail : '-'}
                              </Typography>
                            </Stack>
                          </Stack>
                        </Box>
                      </Stack>
                      <Divider sx={{ mt: 2 }} />
                      <Stack mt={2}>
                        <Typography
                          sx={{ color: '#6C737F' }}
                          variant="overline"
                        >
                          Therapy Details
                        </Typography>
                        <Box mt={2}>
                          <Stack
                            flexDirection="row"
                            justifyContent="space-between"
                            py={1}
                          >
                            <Stack>
                              <Typography variant="subtitle2">Primary Physician</Typography>
                            </Stack>
                            <Stack sx={{ width: '50%' }}>
                              <Typography
                                variant="body2"
                                sx={{ color: '#6C737F' }}
                                noWrap
                              >
                                {patientData?.physicianEntitySet &&
                                patientData?.physicianEntitySet?.length !== 0
                                  ? patientData?.physicianEntitySet
                                      ?.map((physician: any) => physician.physicianName)
                                      .join('')
                                  : '-'}
                              </Typography>
                            </Stack>
                          </Stack>

                          <Stack
                            flexDirection="row"
                            justifyContent="space-between"
                            py={1}
                          >
                            <Stack>
                              <Typography variant="subtitle2">Medical Record Number</Typography>
                            </Stack>
                            <Stack sx={{ width: '50%' }}>
                              <Typography
                                variant="body2"
                                sx={{ color: '#6C737F' }}
                                noWrap
                              >
                                {patientData?.medicalRecordNumber
                                  ? patientData?.medicalRecordNumber
                                  : '-'}
                              </Typography>
                            </Stack>
                          </Stack>

                          <Stack
                            flexDirection="row"
                            justifyContent="space-between"
                            py={1}
                          >
                            <Stack>
                              <Typography variant="subtitle2">Social Security Number</Typography>
                            </Stack>
                            <Stack sx={{ width: '50%' }}>
                              <Typography
                                variant="body2"
                                sx={{ color: '#6C737F' }}
                              >
                                {patientData?.socialSecurityNumber
                                  ? patientData?.socialSecurityNumber
                                      ?.toString()
                                      ?.replace(/^(\d{5})(\d{4})$/, 'XXX-XX-$2')
                                  : '-'}
                              </Typography>
                            </Stack>
                          </Stack>
                          <Stack
                            flexDirection="row"
                            justifyContent="space-between"
                            py={1}
                          >
                            <Stack>
                              <Typography variant="subtitle2">Bed Assignment</Typography>
                            </Stack>
                            <Stack sx={{ width: '50%' }}>
                              <Typography
                                variant="body2"
                                sx={{ color: '#6C737F' }}
                              >
                                {formatPatientData(
                                  patientData?.floorNumber,
                                  patientData?.roomNumber,
                                  patientData?.bedAssignment
                                )}
                              </Typography>
                            </Stack>
                          </Stack>
                        </Box>
                      </Stack>
                      <Divider sx={{ mt: 2 }} />
                      <Stack mt={2}>
                        <Typography
                          sx={{ color: '#6C737F' }}
                          variant="overline"
                        >
                          Medicare Part B Charges To Date
                        </Typography>
                        <Box mt={2}>
                          <Stack
                            flexDirection="row"
                            justifyContent="space-between"
                            py={1}
                          >
                            <Stack>
                              <Typography variant="subtitle2">PT+SLP</Typography>
                            </Stack>
                            <Stack sx={{ width: '50%' }}>
                              <Typography
                                variant="body2"
                                sx={{ color: '#6C737F' }}
                                noWrap
                              >
                                {patientData?.ptSlpCharges || patientData?.ptSlpCharges === 0
                                  ? patientData?.ptSlpCharges
                                  : '-'}
                              </Typography>
                            </Stack>
                          </Stack>

                          <Stack
                            flexDirection="row"
                            justifyContent="space-between"
                            py={1}
                          >
                            <Stack>
                              <Typography variant="subtitle2">OT</Typography>
                            </Stack>
                            <Stack sx={{ width: '50%' }}>
                              <Typography
                                variant="body2"
                                sx={{ color: '#6C737F' }}
                                noWrap
                              >
                                {patientData?.otCharges || patientData?.otCharges === 0
                                  ? patientData?.otCharges
                                  : '-'}
                              </Typography>
                            </Stack>
                          </Stack>
                        </Box>
                      </Stack>
                    </Box>
                  )}
                </Box>
              </Stack>
              <Divider sx={{ my: 1 }} />
            </Grid>
          </Grid>
        </Grid>
      </>
    </Grid>
  );
};

export default PatientInfoDrawer;
